'use client';

import { useCallback, useRef } from 'react';

function getCtx(ref: React.MutableRefObject<AudioContext | null>): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!ref.current) ref.current = new AudioContext();
    if (ref.current.state === 'suspended') ref.current.resume();
    return ref.current;
  } catch {
    return null;
  }
}

export function useChatSounds() {
  const ctxRef        = useRef<AudioContext | null>(null);
  const botPlayedRef  = useRef(false); // play bot sound only once per turn

  // Short ascending "pop" — signals the message was sent
  const playUserSound = useCallback(() => {
    const ctx = getCtx(ctxRef);
    if (!ctx) return;
    try {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(460, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(680, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.09);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.1);
    } catch { /**/ }
  }, []);

  // Soft two-tone chime — signals the bot has responded
  const playBotSoundOnce = useCallback(() => {
    if (botPlayedRef.current) return;
    botPlayedRef.current = true;
    const ctx = getCtx(ctxRef);
    if (!ctx) return;
    try {
      const tones: [number, number][] = [
        [880,  0],     // A5 — first note, starts immediately
        [1047, 0.14],  // C6 — second note, follows 140 ms later
      ];
      for (const [freq, delay] of tones) {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = ctx.currentTime + delay;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.1, t + 0.02);   // fast attack
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.28); // gentle decay
        osc.start(t);
        osc.stop(t + 0.29);
      }
    } catch { /**/ }
  }, []);

  // Call at the start of each user turn to re-arm the bot sound for the next response
  const resetBotSound = useCallback(() => {
    botPlayedRef.current = false;
  }, []);

  return { playUserSound, playBotSoundOnce, resetBotSound };
}
